/* eslint-disable @typescript-eslint/ban-types */
import { findLogService } from "@cervval.js/common/src/services/log.service";
import { getProp } from "@cervval.js/common/src/tools/any.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import { DomTools } from "../tools";
import { bindChildrenSlots } from "./customElement.bindChildren";
import { getElementCssClassesAlertInStyle } from "./customElement.tools";
import { CUSTOM_ELEMENT_CLASS_CONFIG, ConstructorFunction, CustomElementClassConfig, CustomElementConfig, INIT_REFRESH_REASON, WAIT_FOR_AFTER_INIT_RESOLVER } from "./customElement.types";
import { WebComponent } from "./webComponent";
import { TRACE_DOUBLE_REFRESH_THRESHOLD_MS, TRACE_LONG_INIT_THRESHOLD_MS, TRACE_REFRESH_BEFORE_INIT_DONE } from "./webComponent.debug";

export { OverrideCustomElementConfig } from "./customElement.types";

/**
 * CustomElement class Decorator for classes that extend WebComponent
 * 
 * Component registration
 * ----------------------
 * the name provided to @CustomElement will be used to register the component in the window custom elements registry
 * window.customElements.define(config.name, component, config.definitionOptions);
 * 
 * Html Template
 * -------------
 * Inheritance
 * the template is read from CustomElementConfig.template string, 
 * if the 'Child Custom Element' inherits from 'Parent Custom Element' and no template is set on 'Child Custom Element', the template of 'Parent Custom Element' will be used
 * 
 * Shadow DOM / Light DOM
 * -If attachShadow is set on the @CustomElement or provided to the WebComponent constructor, 
 * the component content will be attached to the shadow DOM directly in the constructor
 * -Otherwise, the component content will be placed in a <slot> that will be considered as the light DOM root
 * The WebComponent class has a getContainer function that returns the shadowRoot or the lightDomRoot depending on situation
 * When in light DOM, the lightDomRoot <slot> will be append to the component on the first connectedCallback call (ie when the component is first inserted in th DOM)
 * Which means there is a <slot> component between the WebComponent and its children but it should not be considered regarding css since it is ignored by browsers.
 * 
 * Slot binding
 * You can bind children of a component instance that have slot="slotName" to the <slot name="slotName"> element within the template
 * template.html (passed to @CustomElement) : ... <slot name="slotName"> ...
 * htmlUsingMyComponent.html : ... <myComponent><div slot="slotName" /></myComponent> ...
 * 
 * -When using shadow DOM, the binding is done by the browser.
 * -In light DOM, you need to set bindChildrenSlots to true in the @CustomElement options or in the WebComponent constructor options and customElement will do it.
 * * slot binding in light DOM is experimental
 * 
 * Css Style
 * ---------
 * -If component is attached to shadow DOM, the component does not have access to global style
 * The provided style is injected in the shadow root (with the html template) for each instance of the component
 * -if component is in the light DOM, it can access the global style
 * The provided style is injected in a <style> within the <head> only once and is shared by all instances.
 * As the style is global, there is no default encapsulation and it is the developer's job to ensure styles don't clash between components
 * This can be done by adding a CSS class to the component (with the elementCssClasses parameter) and wrap all the css with this class
 * 
 * myComponent.ts : @CustomElement{... style: require("./myComponent.scss"), elementCssClasses: ["my-component-class"] }
 * myComponent.scss :
 * .my-component-class{
 * ... //my css styles
 * }
 * 
 */
export function CustomElement<T extends ConstructorFunction<WebComponent>>(config: CustomElementConfig): (tConstructor: T) => void {

    // build class config that will be used by constructor    
    const classConfig: CustomElementClassConfig = {
        config,
        template: undefined
    };

    // set style
    if (config.style) {
        let style = DomTools.getStringLoadedFromModule(config.style);

        // Add alert message in style div
        if (config.elementCssClasses) {
            style = `${getElementCssClassesAlertInStyle(config.elementCssClasses)} \t\n\t\n ${style ?? ""}`;
        }

        classConfig.style = style;
    }

    // return the class decorator factory
    return (tConstructor: T): void => {
        // register class name
        classConfig.className = tConstructor.name;

        // register parent config
        classConfig.parentConfig = tConstructor.prototype[CUSTOM_ELEMENT_CLASS_CONFIG];

        // register class config
        tConstructor.prototype[CUSTOM_ELEMENT_CLASS_CONFIG] = classConfig;

        // create a template shared by all instances
        const htmlTemplate = getTemplateForClass(classConfig);
        if (htmlTemplate) {
            classConfig.template = document.createElement("template");
            classConfig.template.innerHTML = htmlTemplate;
        }

        // disable refresh until init is done
        classConfig.original_refresh = tConstructor.prototype.refresh;// function is restored once in DOM
        tConstructor.prototype.refresh = function () {
            if (this) {
                const that: WebComponent = this;
                if (!that.isInitDone) {
                    // do not call refresh until init is done
                    if (getProp(that, TRACE_REFRESH_BEFORE_INIT_DONE)) {
                        console.info("refresh is deactivated until init is done", that);
                    }
                } else {
                    // eslint-disable-next-line prefer-rest-params
                    (classConfig.original_refresh as anyReallyAny)!.apply(that, arguments);
                }
            }
        };

        // override connectedCallback
        const originalConnectedCallback = tConstructor.prototype.connectedCallback || function () { };
        tConstructor.prototype.connectedCallback = function () {// Element is added to the DOM

            if (this && !this.wasConnected) { // Do not append again when (re)connecting the component (for example to shadow root)
                const that: WebComponent = this;

                // Update was connected value
                that.wasConnected = true;

                if (!that.isInitDone) {
                    appendLightDOMRootToComponent(classConfig, that);
                    initComponent(classConfig, that, { originalConnectedCallback });
                    // Note that we don't return the promise since connectedCallback is not an async function in API definition
                    // The Promise catch is inside the initComponent function
                } else {
                    // isInitDone can be true if developer called callInitByAnticipation before adding component to the DOM
                    originalConnectedCallback.call(this);
                }

            } else {
                originalConnectedCallback.call(this);
            }

            // emit connected on listeners
            const connectionListeners = (this as WebComponent).connectionListeners;
            if (connectionListeners) {
                for (const l of connectionListeners) {
                    l.connected?.();
                }
            }
        };

        const originalDisconnectedCallback = tConstructor.prototype.disconnectedCallback || function () { };
        tConstructor.prototype.disconnectedCallback = function () {// Element is detached from DOM
            originalDisconnectedCallback.call(this);

            // emit disconnected on listeners
            const connectionListeners = (this as WebComponent).connectionListeners;
            if (connectionListeners) {
                for (const l of connectionListeners) {
                    l.disconnected?.();
                }
            }
        };

        // provide access to i18n and cache for all instances
        const i18nCache: { cache?: object } = {};
        Object.defineProperty(tConstructor.prototype, "i18n", {
            get: function () {
                if (!i18nCache.cache) {
                    i18nCache.cache = appendI18nFromClassConfigAndAncestors(classConfig, {});
                }
                return i18nCache.cache;
            }
        });

        // register the tag name in order to be able to use the custom element in the html
        window.customElements.define(config.name, tConstructor, config.definitionOptions ?? undefined);
    };
}

/**
 * Append lightDomRoot children to the component
 * * We can't do this in the constructor because doing so sometimes lead to not having access to component's variables
 * * BGS : I did not find any explanation to this !
 */
function appendLightDOMRootToComponent(classConfig: CustomElementClassConfig, wc: WebComponent): void {
    if (wc.lightDomRoot && wc.lightDomRoot !== wc) {

        // store initial children in order to add them to 'parsedInnerHtmlTarget' if needed
        const initialChildren = Array.from(wc.childNodes);

        const lightDomRoot = wc.lightDomRoot;
        // light dom root is now the component itself
        wc.lightDomRoot = wc;

        // append lightDomRoot children to the component
        //* We can't do this in the constructor because doing so sometimes lead to not having access to component's variables
        //* BGS : I did not find any explanation to this !
        lightDomRoot.childNodes.forEach((child) => {
            wc.append(child);
        });

        // Bind children slots (this is done by the browser when using shadow DOM but not in light DOM)
        //* bindChildrenSlots in light DOM is experimental
        if (wc.customElementOptions?.bindChildrenSlots ?? classConfig.config.bindChildrenSlots) {
            bindChildrenSlots(wc);
        }

        // reparent initial children if needed
        if (initialChildren.length) {
            const target = wc.getParsedInnerHtmlTarget();
            if (target) {
                // if a target is provided, we reparent every child with the provided target
                for (const child of initialChildren) {
                    if (child) {
                        target.appendChild(child);
                    }
                }
            }
        }
    }
}

/**
 * Initialize the component
 * @returns Promise<void> if init returns a Promise, void otherwise
 */
function initComponent(classConfig: CustomElementClassConfig, wc: WebComponent, options?: { originalConnectedCallback?: () => void, doNotRefreshAfterInit?: boolean }): void | Promise<void> {

    // Before init() - lifecycle event
    wc.onBeforeInit();

    // write a message in console if init is too long
    const initTimeout = getProp(wc, TRACE_LONG_INIT_THRESHOLD_MS) ? setTimeout(() => {
        findLogService()?.warn("init takes too long, you should not have any network or processing within init", wc);
    }, getProp(wc, TRACE_LONG_INIT_THRESHOLD_MS)) : undefined;

    /**
     * operations to do after init call (regardless if it returns a promise or not)
     */
    function afterInit(): void {

        if (initTimeout) {
            // cancel display of "init takes too long" message
            clearTimeout(initTimeout);
        }

        // prevent init from being called twice
        wc.init = function () {
            console.info("init can NOT be called several times");
        };

        wc.isInitDone = true;

        const threshold = getProp<number | undefined>(wc, TRACE_DOUBLE_REFRESH_THRESHOLD_MS);
        if (threshold) {
            // trace double refresh
            let lastRefresh = 0;
            let lastRefreshStack: string | undefined = undefined;
            wc.refresh = function (reason: string) {
                if (reason !== INIT_REFRESH_REASON) {
                    // we ignore the init refresh because we can't control when it is done and it is "acceptable" if we have the init refresh and another one just after
                    const instant = performance.now();
                    if (instant - lastRefresh < threshold) {
                        console.info("last refresh stack trace was", lastRefreshStack);
                        console.info("current refresh stack trace was", new Error().stack);
                        console.info("last refresh call was " + (instant - lastRefresh) + " ms ago", wc);
                    }
                    lastRefreshStack = new Error().stack;

                    lastRefresh = instant;
                }

                // eslint-disable-next-line prefer-rest-params
                (classConfig.original_refresh as anyReallyAny)!.call(wc, arguments);
            };
        } else {
            // reinstall originalRefresh in order to have a clean stack trace
            wc.refresh = classConfig.original_refresh!;
        }

        // After init() - lifecycle callback
        wc.onAfterInit();

        if (options?.originalConnectedCallback) {
            options.originalConnectedCallback.call(wc);
        }

        // resolve WaitForAfterInit Promise
        getProp<Function>(wc, WAIT_FOR_AFTER_INIT_RESOLVER)();

        // refresh() - lifecycle callback
        const doNotRefreshAfterInit = options?.doNotRefreshAfterInit ?? wc.customElementOptions?.doNotRefreshAfterInit ?? classConfig.config.doNotRefreshAfterInit;
        if (doNotRefreshAfterInit !== true) {
            wc.refresh(INIT_REFRESH_REASON);
        }
    }// end of function afterInit

    // Init component
    const initPromise = wc.init();
    if (initPromise) {
        // if init returned a promise execute originalConnectedCallback once promise is resolved
        return initPromise.catch((e) => {
            findLogService()?.error(e);
            // we continue the page process even if init failed
        }).then(() => {
            return afterInit();
        }).catch((e) => {
            findLogService()?.error(e);
        });
    } else {
        return afterInit();
    }
}

/**
 * Appends the i18n found in the classConfig and its ancestors to result (does not change result)
 * @returns an object corresponding to the merge of result and the i18n from configs
 */
function appendI18nFromClassConfigAndAncestors(classConfig: CustomElementClassConfig, result: object): object {
    if (classConfig.parentConfig) {
        result = appendI18nFromClassConfigAndAncestors(classConfig.parentConfig, result);
    }
    if (classConfig.config.i18n) {
        // get i18n objects from config and load in cache
        const i18nArray = Array.isArray(classConfig.config.i18n) ? classConfig.config.i18n : [classConfig.config.i18n];
        for (const i18nInstance of i18nArray) {
            let i18nObject: object | undefined = undefined;
            if (typeof i18nInstance === "function") {
                i18nObject = i18nInstance();
            } else if (typeof i18nInstance === "object") {
                i18nObject = i18nInstance;
            }
            if (i18nObject) {
                result = { ...result, ...i18nObject };
            }
        }
    }
    return result;
}

/**
 * @returns the template found in classConfig if defined or the template found in its first ancestor with a template
 */
function getTemplateForClass(classConfig: CustomElementClassConfig): string | undefined {
    if (classConfig.config.template != null) {
        return classConfig.config.template;
    }
    if (classConfig.parentConfig) {
        return getTemplateForClass(classConfig.parentConfig);
    }
    return undefined;
}


/**
 * If you want init to be called before a web component is added to the DOM, you can use callInitByAnticipation
 * associated lifecycle events will also be triggered (beforeInit, afterInit and refresh("init"))
 * 
 * Init and associated lifecycle events won't be called again after once component is added to the DOM
 * 
 * @param wc the web component to initialize
 * @param options
 * @param options.doNotRefreshAfterInit if true refresh will not be called (you don't need to set this to true if doNotRefreshAfterInit is set to true in the CustomElement decorator)
 */
export function callInitByAnticipation(wc: WebComponent, options?: { doNotRefreshAfterInit?: boolean }): void | Promise<void> {
    const classConfig = getProp(wc, CUSTOM_ELEMENT_CLASS_CONFIG) as CustomElementClassConfig;
    appendLightDOMRootToComponent(classConfig, wc);
    return initComponent(classConfig, wc, options);
}